import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';

function AgriHome({ filters, loading, setLoading, isStaging, onDrilldown }) {
  const [kpis, setKpis] = useState({ alert_count: 0, market_health: 50, activity_count: 0 });
  const [volumeSentiment, setVolumeSentiment] = useState({ labels: [], volume: [], sentiment: [] });
  const [distribution, setDistribution] = useState({ labels: [], data: [] });
  const [marketShare, setMarketShare] = useState({ labels: [], data: [] });
  const [competitivePos, setCompetitivePos] = useState([]);
  const [drivers, setDrivers] = useState({ labels: [], data: [] });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });

    Promise.all([
      axios.get(`/api/agri/home/kpis?${params}`),
      axios.get(`/api/agri/home/volume-sentiment?${params}`),
      axios.get(`/api/agri/home/conversation-distribution?${params}`),
      axios.get(`/api/agri/home/market-share?${params}`),
      axios.get(`/api/agri/home/competitive-position?${params}`),
      axios.get(`/api/agri/home/conversation-drivers?${params}`),
    ])
      .then(([kpiRes, vsRes, distRes, msRes, cpRes, drRes]) => {
        setKpis(kpiRes.data);
        setVolumeSentiment(vsRes.data);
        setDistribution(distRes.data);
        setMarketShare(msRes.data);
        setCompetitivePos(cpRes.data);
        setDrivers(drRes.data);
      })
      .catch(err => console.error('Error loading Agri Home:', err))
      .finally(() => setLoading(false));
  }, [filters, setLoading]);

  const cardStyle = {
    background: '#fff', borderRadius: 8, padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center', minWidth: 200,
  };
  const gridStyle = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 16, marginTop: 16,
  };
  const plotLayout = { margin: { t: 30, b: 40, l: 50, r: 20 }, height: 300, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent' };

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#e74c3c' }}>{kpis.alert_count}</div>
          <div style={{ fontSize: 13, color: '#666' }}>Alert Count</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#2ecc71' }}>{kpis.activity_count}</div>
          <div style={{ fontSize: 13, color: '#666' }}>Activity Count</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#3498db' }}>{kpis.market_health}%</div>
          <div style={{ fontSize: 13, color: '#666' }}>Market Health</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div style={gridStyle}>
        {/* Volume & Sentiment Trend */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Volume & Sentiment Trend</h3>
          <Plot
            data={[
              { x: volumeSentiment.labels, y: volumeSentiment.volume, type: 'bar', name: 'Volume', marker: { color: '#3498db' } },
              { x: volumeSentiment.labels, y: volumeSentiment.sentiment, type: 'scatter', mode: 'lines+markers', name: 'Sentiment', yaxis: 'y2', marker: { color: '#e74c3c' } },
            ]}
            layout={{ ...plotLayout, yaxis: { title: 'Volume' }, yaxis2: { title: 'Sentiment', overlaying: 'y', side: 'right' } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        </div>

        {/* Conversation Distribution */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Conversation Distribution</h3>
          <Plot
            data={[{ labels: distribution.labels, values: distribution.data, type: 'pie', hole: 0.4 }]}
            layout={{ ...plotLayout, showlegend: true }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        </div>

        {/* Market Share */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Market Share</h3>
          <Plot
            data={[{ x: marketShare.labels, y: marketShare.data, type: 'bar', marker: { color: '#2ecc71' } }]}
            layout={plotLayout}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        </div>

        {/* Competitive Position */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Competitive Position</h3>
          {competitivePos.length > 0 ? (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  <th style={{ textAlign: 'left', padding: '6px' }}>Company</th>
                  <th style={{ textAlign: 'right', padding: '6px' }}>Mentions</th>
                  <th style={{ textAlign: 'right', padding: '6px' }}>Share %</th>
                </tr>
              </thead>
              <tbody>
                {competitivePos.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '6px' }}>{r.brand}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{r.mentions}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{r.share}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ color: '#999', padding: 20 }}>No data</div>
          )}
        </div>

        {/* Conversation Drivers */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Conversation Drivers</h3>
          <Plot
            data={[{ x: drivers.data, y: drivers.labels, type: 'bar', orientation: 'h', marker: { color: '#9b59b6' } }]}
            layout={{ ...plotLayout, yaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
}

export default AgriHome;
